import * as yup from 'yup';
import { TFunction } from 'i18next';

import { internationalPhoneValidationSchema } from '@common';
import { emailRegex } from '@/constants/email';
import { HiringStatus, OnboardingStep } from './types';
import { createAddressValidationSchema } from '../AddressValidation/helpers';

const getNewHireDemographicsSchema = (t: TFunction) =>
  yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    personalEmail: yup
      .string()
      .matches(emailRegex, t('pages:onboarding.schema.invalidEmail'))
      .required(),
    address: createAddressValidationSchema(true),
    phone: yup
      .object()
      .shape({
        phoneNumber: yup
          .string()
          .required(t('pages:onboarding.schema.phoneNumberRequired'))
          .test(
            'is-valid-phone',
            t('pages:onboarding.schema.invalidPhoneNumber'),
            function (value) {
              if (!value) return false;

              try {
                internationalPhoneValidationSchema({
                  required: true,
                }).validateSync(value);
                return true;
              } catch {
                return false;
              }
            }
          ),
        country: yup.string().optional(),
      })
      .nullable()
      .required(t('pages:onboarding.schema.phoneNumberRequired')),
  });

const getExistingEmployeeDemographicsSchema = (t: TFunction) =>
  yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    personalEmail: yup
      .string()
      .matches(emailRegex, t('pages:onboarding.schema.invalidEmail'))
      .notRequired(),
    address: createAddressValidationSchema(false),
    phone: yup
      .object()
      .shape({
        phoneNumber: yup
          .string()
          .test(
            'is-valid-phone',
            t('pages:onboarding.schema.invalidPhoneNumber'),
            function (value) {
              if (!value) return true;

              try {
                internationalPhoneValidationSchema({
                  required: false,
                }).validateSync(value);
                return true;
              } catch {
                return false;
              }
            }
          ),
        country: yup.string().optional(),
      })
      .nullable()
      .optional(),
  });

const getHrisEmployeeDemographicsSchema = () =>
  yup.object().shape({
    firstName: yup.string().notRequired(),
    lastName: yup.string().notRequired(),
    personalEmail: yup.string().notRequired(),
    address: createAddressValidationSchema(false),
    phone: yup.string().notRequired(),
  });

const getDemographicsSchema = (t: TFunction) =>
  yup.object().shape({
    demographics: yup
      .object()
      .when('hiringStatus', ([hiringStatus]) =>
        hiringStatus === HiringStatus.New
          ? getNewHireDemographicsSchema(t)
          : hiringStatus === HiringStatus.Existing
            ? getExistingEmployeeDemographicsSchema(t)
            : getHrisEmployeeDemographicsSchema()
      ),
  });

const getNewHireEmployeeInformationSchema = (t: TFunction) =>
  yup.object().shape({
    startDate: yup.date().required(),
    jobTitle: yup.string().required(),
    email: yup
      .string()
      .matches(emailRegex, t('pages:onboarding.schema.invalidEmail'))
      .required(),
  });

const getExistingEmployeeEmployeeInformationSchema = (t: TFunction) =>
  yup.object().shape({
    startDate: yup.date().notRequired(),
    jobTitle: yup.string().notRequired(),
    email: yup
      .string()
      .matches(emailRegex, t('pages:onboarding.schema.invalidEmail'))
      .required(),
  });

const hrisEmployeeEmployeeInformationSchema = yup.object().shape({
  startDate: yup.date().notRequired(),
  jobTitle: yup.string().notRequired(),
  email: yup.string().notRequired(),
});

const getEmployeeInformationSchema = (t: TFunction) =>
  yup.object().shape({
    employeeInformation: yup
      .object()
      .when('hiringStatus', ([hiringStatus]) =>
        hiringStatus === HiringStatus.New
          ? getNewHireEmployeeInformationSchema(t)
          : hiringStatus === HiringStatus.Existing
            ? getExistingEmployeeEmployeeInformationSchema(t)
            : hrisEmployeeEmployeeInformationSchema
      ),
  });

export const getOnboardingSchema = (t: TFunction) => {
  yup.setLocale({
    mixed: {
      required: t('pages:onboarding.schema.requiredField'),
    },
  });

  return yup.lazy(({ currentStep }) => {
    return currentStep === OnboardingStep.Demographics
      ? getDemographicsSchema(t)
      : getEmployeeInformationSchema(t);
  });
};
